import autobahn from '../../lightweight-autobahn/src/autobahn';

/**
 * Connection State
 */
let connected = false;
let connecting = false;

let connectionOptions = {};

let currentConnection = null;
let currentSession = null;

/**
 * Handlers
 */
let handlersCounter = 0;

const onConnectHandlers = {};
const onDisconnectHandlers = {};

/**
 * addHandler - Adds a handler to a specific handlers object, returning a function that removes the handler when called
 * @private
 */
function addHandler(handlersObject, fn) {
    if (typeof fn !== 'function') {
        throw new Error('Invalid function supplied to onConnect');
    }

    const newHandlersObject = handlersObject;
    newHandlersObject[++handlersCounter] = fn;
    const fnIndex = handlersCounter;
    return () => {
        delete newHandlersObject[fnIndex];
    };
}


/**
 * callHandlers - Calls all handlers for a specific handlers object, with specified parameters
 * @private
 */
function callHandlers(handlersObject, params = {}) {
    for (const handlerIndex of Object.keys(handlersObject)) {
        if (typeof handlersObject[handlerIndex] === 'function') {
            handlersObject[handlerIndex](...params);
        }
    }
}

/**
 * connect - Connects with supplied options; this will replace stored transport settings
 *
 * @param  {object} options                   connection options to be passed to an underlying autobahn Connection, some options are overridden to keep connection up indefinitely
 * @return {bool}                             true if the library begins connecting, false if it was already connecting or connected
 */
export function connect(newOptions) {
    // validate options
    if (!newOptions) {
        throw new Error('Invalid options supplied to `connect()` ');
    }

    // merge defaults
    const options = {
        ...newOptions,

        use_es6_promises: true,
        max_retries: -1,
        retry_if_unreachable: true,
    };

    // save options
    connectionOptions = options;

    // ignore connection attempt
    if (currentConnection || connected || connecting) {
        return false;
    }

    // set connection as connecting
    connecting = true;
    currentConnection = new autobahn.Connection(options);

    // attach handlers
    currentConnection.onopen = function (session) {
        // set internal state
        connecting = false;
        connected = true;
        currentSession = session;

        // call onConnectHandlers
        callHandlers(onConnectHandlers);
    };

    // disconnects
    currentConnection.onclose = function (reason, details) {
        // internal state
        connecting = currentConnection && currentConnection.isRetrying || details && details.will_retry || false;
        connected = false;
        currentSession = null;

        // call onDisconnectHandlers
        callHandlers(onDisconnectHandlers);

        // automatically reconnect if there is are no active retry efforts
        if (!connecting && !connected) {
            currentConnection = null;
            connect(connectionOptions);
        }
    };

    // open the connection
    currentConnection.open();

    // done
    return true;
}

/**
 * disconnect - drop current connection - will disconnect the current session and another reconnect attempt will be made automatically
 *
 * @param  {string} reason  optional WAMP URI providing a closing reason
 * @param  {string} details optional (human readable) closing message
 * @return {boolean|string}         string on connection closing error, true on success
 */
export function disconnect(reason, details) {
    if ((connected || connecting) && currentConnection && currentConnection.close) {
        // close the connection
        const result = currentConnection.close(reason, details);
        return typeof result === 'undefined' && true || result;
    }
    return false;
}

/**
 * isConnected - check whether the library is connected
 *
 * @return {boolean}  true if the library is connected
 */
export function isConnected() {
    return !!connected;
}

/**
 * isConnecting - check whether the library is connecting
 *
 * @return {boolean}  true if the library is connecting
 */

export function isConnecting() {
    return !!connecting;
}


/**
 * onConnect - attach a handler to be executed once a session is established
 *
 * @param  {function} fn    handler
 * @return {function}       returns a function which removes the handler when called
 */
export function onConnect(fn) {
    return addHandler(onConnectHandlers, fn);
}

/**
 * onDisconnect - attach a handler to be executed once a session is dropped or connection is closed
 *
 * @param  {function} fn    handler
 * @return {function}       returns a function which removes the handler when called
 */
export function onDisconnect(fn) {
    return addHandler(onDisconnectHandlers, fn);
}


/**
 * getSession - (private) get current session
 *
 * @return {object}  null or autobahn subscription instance
 */
export function getSession() {
    return currentSession;
}


/**
 * getConnection - (private) get current connection
 *
 * @return {object}  null or autobahn connection instance
 */
export function getConnection() {
    return currentConnection;
}



// WEBPACK FOOTER //
// ./src/em-shared-session/src/connection.js